/**
 * Shared emission factors that are not already covered by the dedicated
 * accommodation, electricity, or refrigerant factor files.
 *
 * Calculation logic stays in the backend or existing calling layer; this file
 * only owns factor values and lightweight lookup helpers.
 */

export interface Scope3Factor {
  category: string;
  subcategory: string;
  unit: string;
  kgco2e_per_unit: number;
  description: string;
}

export const BUSINESS_TRAVEL_FACTORS: Scope3Factor[] = [
  {
    category: "business_travel",
    subcategory: "flight_domestic",
    unit: "passenger_km",
    kgco2e_per_unit: 0.14825,
    description: "Domestic flight (economy class)",
  },
  {
    category: "business_travel",
    subcategory: "flight_short_haul_intl",
    unit: "passenger_km",
    kgco2e_per_unit: 0.15072,
    description: "Short-haul international flight (< 3,700 km)",
  },
  {
    category: "business_travel",
    subcategory: "flight_long_haul_intl",
    unit: "passenger_km",
    kgco2e_per_unit: 0.18495,
    description: "Long-haul international flight (> 3,700 km)",
  },
  {
    category: "business_travel",
    subcategory: "train_national",
    unit: "passenger_km",
    kgco2e_per_unit: 0.04443,
    description: "National rail",
  },
  {
    category: "business_travel",
    subcategory: "train_international",
    unit: "passenger_km",
    kgco2e_per_unit: 0.00563,
    description: "International rail (e.g., Eurostar)",
  },
  {
    category: "business_travel",
    subcategory: "taxi",
    unit: "passenger_km",
    kgco2e_per_unit: 0.20037,
    description: "Taxi (regular)",
  },
  {
    category: "business_travel",
    subcategory: "bus",
    unit: "passenger_km",
    kgco2e_per_unit: 0.13034,
    description: "Coach or bus (average)",
  },
  {
    category: "business_travel",
    subcategory: "car_rental_petrol",
    unit: "passenger_km",
    kgco2e_per_unit: 0.16272,
    description: "Rental car (petrol, average)",
  },
  {
    category: "business_travel",
    subcategory: "car_rental_diesel",
    unit: "passenger_km",
    kgco2e_per_unit: 0.17304,
    description: "Rental car (diesel, average)",
  },
];

export const COMMUTING_FACTORS: Scope3Factor[] = [
  {
    category: "employee_commuting",
    subcategory: "car_petrol_avg",
    unit: "passenger_km",
    kgco2e_per_unit: 0.16272,
    description: "Car (petrol, average)",
  },
  {
    category: "employee_commuting",
    subcategory: "car_diesel_avg",
    unit: "passenger_km",
    kgco2e_per_unit: 0.17304,
    description: "Car (diesel, average)",
  },
  {
    category: "employee_commuting",
    subcategory: "bus",
    unit: "passenger_km",
    kgco2e_per_unit: 0.13034,
    description: "Local bus",
  },
  {
    category: "employee_commuting",
    subcategory: "train_local",
    unit: "passenger_km",
    kgco2e_per_unit: 0.04443,
    description: "Local train/metro",
  },
  {
    category: "employee_commuting",
    subcategory: "motorcycle",
    unit: "passenger_km",
    kgco2e_per_unit: 0.113,
    description: "Motorcycle (average)",
  },
  {
    category: "employee_commuting",
    subcategory: "taxi",
    unit: "passenger_km",
    kgco2e_per_unit: 0.211,
    description: "Taxi",
  },
];

export const SPEND_BASED_FACTORS: Scope3Factor[] = [
  {
    category: "spend_based",
    subcategory: "office_supplies",
    unit: "usd",
    kgco2e_per_unit: 0.292,
    description: "Office supplies and stationery (EPA USEEIO v1.2 - NAICS 339940)",
  },
  {
    category: "spend_based",
    subcategory: "it_equipment",
    unit: "usd",
    kgco2e_per_unit: 0.488,
    description: "IT equipment and electronics (EPA USEEIO v1.2 - Computers/Electronics aggregate)",
  },
  {
    category: "spend_based",
    subcategory: "professional_services",
    unit: "usd",
    kgco2e_per_unit: 0.067,
    description: "Professional services - consulting, legal, accounting (EPA USEEIO v1.2)",
  },
  {
    category: "spend_based",
    subcategory: "marketing_advertising",
    unit: "usd",
    kgco2e_per_unit: 0.114,
    description: "Marketing and advertising (EPA USEEIO v1.2 - Advertising/PR services)",
  },
  {
    category: "spend_based",
    subcategory: "catering_food",
    unit: "usd",
    kgco2e_per_unit: 0.155,
    description: "Catering and food services (EPA USEEIO v1.2 - NAICS 722320)",
  },
  {
    category: "spend_based",
    subcategory: "furniture_fixtures",
    unit: "usd",
    kgco2e_per_unit: 0.147,
    description: "Furniture and fixtures (EPA USEEIO v1.2 - NAICS 423210 wholesale)",
  },
  {
    category: "spend_based",
    subcategory: "other",
    unit: "usd",
    kgco2e_per_unit: 0.098,
    description: "Other business expenses (EPA USEEIO v1.2)",
  },
];

export const FREIGHT_FACTORS: Scope3Factor[] = [
  {
    category: "freight_upstream",
    subcategory: "road_freight_hgv",
    unit: "tonne_km",
    kgco2e_per_unit: 0.10163,
    description: "Road freight (HGV, average)",
  },
  {
    category: "freight_upstream",
    subcategory: "road_freight_van",
    unit: "tonne_km",
    kgco2e_per_unit: 0.63823,
    description: "Road freight (van, average)",
  },
  {
    category: "freight_upstream",
    subcategory: "air_freight",
    unit: "tonne_km",
    kgco2e_per_unit: 0.89939,
    description: "Air freight",
  },
  {
    category: "freight_upstream",
    subcategory: "sea_freight",
    unit: "tonne_km",
    kgco2e_per_unit: 0.01612,
    description: "Sea freight (container ship)",
  },
  {
    category: "freight_upstream",
    subcategory: "rail_freight",
    unit: "tonne_km",
    kgco2e_per_unit: 0.02779,
    description: "Rail freight",
  },
  {
    category: "freight_upstream",
    subcategory: "courier_small_parcel",
    unit: "parcel",
    kgco2e_per_unit: 0.244,
    description: "Small parcel delivery (average UK)",
  },
  {
    category: "freight_upstream",
    subcategory: "public_bus",
    unit: "tonne_km",
    kgco2e_per_unit: 0.089,
    description: "Public bus transport (goods via bus service)",
  },
  {
    category: "freight_downstream",
    subcategory: "road_freight_hgv",
    unit: "tonne_km",
    kgco2e_per_unit: 0.10163,
    description: "Road freight (HGV, average)",
  },
  {
    category: "freight_downstream",
    subcategory: "road_freight_van",
    unit: "tonne_km",
    kgco2e_per_unit: 0.63823,
    description: "Road freight (van, average)",
  },
  {
    category: "freight_downstream",
    subcategory: "air_freight",
    unit: "tonne_km",
    kgco2e_per_unit: 0.89939,
    description: "Air freight",
  },
  {
    category: "freight_downstream",
    subcategory: "sea_freight",
    unit: "tonne_km",
    kgco2e_per_unit: 0.01612,
    description: "Sea freight (container ship)",
  },
  {
    category: "freight_downstream",
    subcategory: "rail_freight",
    unit: "tonne_km",
    kgco2e_per_unit: 0.02779,
    description: "Rail freight",
  },
  {
    category: "freight_downstream",
    subcategory: "courier_small_parcel",
    unit: "parcel",
    kgco2e_per_unit: 0.244,
    description: "Small parcel delivery (average UK)",
  },
  {
    category: "freight_downstream",
    subcategory: "public_bus",
    unit: "tonne_km",
    kgco2e_per_unit: 0.089,
    description: "Public bus transport (goods via bus service)",
  },
];

export const WASTE_FACTORS: Scope3Factor[] = [
  {
    category: "waste",
    subcategory: "landfill_mixed",
    unit: "tonne",
    kgco2e_per_unit: 520.33,
    description: "C&I mixed waste to landfill (DEFRA 2024)",
  },
  {
    category: "waste",
    subcategory: "recycled_mixed",
    unit: "tonne",
    kgco2e_per_unit: 6.41,
    description: "Mixed recycling, closed-loop (DEFRA 2024)",
  },
  {
    category: "waste",
    subcategory: "incineration",
    unit: "tonne",
    kgco2e_per_unit: 6.41,
    description: "Waste combustion/incineration (DEFRA 2024)",
  },
  {
    category: "waste",
    subcategory: "composting",
    unit: "tonne",
    kgco2e_per_unit: 8.88,
    description: "Organic waste composting (DEFRA 2024)",
  },
  {
    category: "waste",
    subcategory: "ewaste",
    unit: "tonne",
    kgco2e_per_unit: 6.41,
    description: "WEEE open-loop recycling (DEFRA 2024)",
  },
  {
    category: "waste",
    subcategory: "hazardous_disposal",
    unit: "tonne",
    kgco2e_per_unit: 1074,
    description: "Hazardous waste high-temperature incineration",
  },
];

export const WATER_FACTORS = {
  supply_kgco2e_per_cubic_meter: 0.344,
  treatment_kgco2e_per_cubic_meter: 0.708,
} as const;

export const HOMEWORKING_FACTORS = {
  kgco2e_per_hour: 0.461,
  hours_per_day: 8,
  working_days_per_year: 250,
} as const;

const SCOPE3_FACTORS = [
  ...BUSINESS_TRAVEL_FACTORS,
  ...COMMUTING_FACTORS,
  ...SPEND_BASED_FACTORS,
  ...FREIGHT_FACTORS,
  ...WASTE_FACTORS,
];

export function getScope3Factor(category: string, subcategory: string): number | null {
  const factor = SCOPE3_FACTORS.find((item) => item.category === category && item.subcategory === subcategory);
  return factor ? factor.kgco2e_per_unit : null;
}

